import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import api from "services/api";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";
import SettingsSection from "./components/SettingsSection";
import SaveBar from "./components/SaveBar";

// Keys of each group must match GROUPS in documents_app/views/llm_models_views.py: a group is saved as a whole.
const GROUPS = {
  main: ["default_model", "model_version"],
  validation: [
    "validation_mode",
    "dual_pass_primary_model",
    "dual_pass_primary_model_version",
    "dual_pass_validation_model",
    "dual_pass_validation_model_version",
  ],
  product_match: ["product_match_model", "product_match_model_version"],
};

const VERSION_HINTS = {
  openai: "pvz. gpt-6-luna, gpt-5-mini",
  claude: "pvz. claude-haiku-4-5-20251001",
  gemini: "pvz. gemini-3.1-flash-lite, gemini-3-flash-preview",
};

function ProviderSelect({ label, value, onChange, withAzure }) {
  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || "openai"}
        onChange={(event) => onChange(event.target.value)}
        label={label}
      >
        <MenuItem value="openai">OpenAI</MenuItem>
        <MenuItem value="claude">Anthropic Claude</MenuItem>
        <MenuItem value="gemini">Google Gemini</MenuItem>
        {withAzure && (
          <MenuItem value="azure_content_understanding">Azure Content Understanding</MenuItem>
        )}
      </Select>
    </FormControl>
  );
}

ProviderSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  withAzure: PropTypes.bool,
};

function ModelsTab() {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [values, setValues] = useState(null);
  const [meta, setMeta] = useState({ companies: 0, differing: [] });
  // Groups that will be written to every company on save
  const [applyGroups, setApplyGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/api/llm-models/")
      .then((response) => {
        setValues(response.data.models);
        setMeta({ companies: response.data.companies, differing: response.data.differing_groups });
      })
      .catch(() => showSnackbar(t("stLoadFailed"), "error"));
  }, []);

  if (!values) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const groupOf = (key) => Object.keys(GROUPS).find((group) => GROUPS[group].includes(key));
  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    const group = groupOf(key);
    setApplyGroups((previous) => (previous.includes(group) ? previous : [...previous, group]));
  };
  const toggleGroup = (group) => (event) =>
    setApplyGroups((previous) =>
      event.target.checked ? [...previous, group] : previous.filter((g) => g !== group)
    );

  const versionField = (key, provider) => (
    <TextField
      fullWidth
      label={t("stModelVersion")}
      value={values[key] || ""}
      onChange={(event) => set(key)(event.target.value)}
      helperText={VERSION_HINTS[provider] || ""}
    />
  );

  const modelRow = (modelKey, versionKey, label) => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <ProviderSelect label={label} value={values[modelKey]} onChange={set(modelKey)} />
      </Grid>
      <Grid item xs={12} sm={6}>
        {versionField(versionKey, values[modelKey])}
      </Grid>
    </Grid>
  );

  const applyControl = (group) => (
    <Box>
      {meta.differing.includes(group) && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t("stModelsDiffer")}
        </Alert>
      )}
      <FormControlLabel
        control={<Checkbox checked={applyGroups.includes(group)} onChange={toggleGroup(group)} />}
        label={t("stApplyToAll").replace("{count}", meta.companies)}
      />
    </Box>
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = applyGroups
        .flatMap((group) => GROUPS[group])
        .reduce((acc, key) => ({ ...acc, [key]: values[key] || "" }), {});
      await api.put("/api/llm-models/", payload);
      setMeta((previous) => ({
        ...previous,
        differing: previous.differing.filter((group) => !applyGroups.includes(group)),
      }));
      setApplyGroups([]);
      showSnackbar(t("stSaved"), "success");
    } catch (error) {
      const data = error.response?.data;
      showSnackbar(
        data?.detail || (data && Object.values(data).join(" ")) || t("stSaveFailed"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Alert severity="info" sx={{ mt: 3 }}>
        {t("stModelsHint").replace("{count}", meta.companies)}
      </Alert>

      <SettingsSection title={t("stMainModel")} description={t("stMainModelHint")}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ProviderSelect
              label={t("stProvider")}
              value={values.default_model}
              onChange={set("default_model")}
              withAzure
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {values.default_model !== "azure_content_understanding" &&
              versionField("model_version", values.default_model)}
          </Grid>
        </Grid>
        {applyControl("main")}
      </SettingsSection>

      <SettingsSection title={t("stValidation")} description={t("stValidationHint")}>
        <FormControl fullWidth>
          <InputLabel>{t("stValidationMode")}</InputLabel>
          <Select
            value={values.validation_mode || "off"}
            onChange={(event) => set("validation_mode")(event.target.value)}
            label={t("stValidationMode")}
          >
            <MenuItem value="off">{t("stValidationOff")}</MenuItem>
            <MenuItem value="targeted">{t("stValidationTargeted")}</MenuItem>
            <MenuItem value="dual">{t("stValidationDual")}</MenuItem>
          </Select>
        </FormControl>
        {values.validation_mode === "dual" &&
          modelRow(
            "dual_pass_primary_model",
            "dual_pass_primary_model_version",
            t("stPrimaryModel")
          )}
        {values.validation_mode !== "off" &&
          modelRow(
            "dual_pass_validation_model",
            "dual_pass_validation_model_version",
            t("stValidationModel")
          )}
        {applyControl("validation")}
      </SettingsSection>

      <SettingsSection title={t("stProductMatching")} description={t("stProductMatchingHint")} last>
        {modelRow("product_match_model", "product_match_model_version", t("stProvider"))}
        {applyControl("product_match")}
      </SettingsSection>

      <SaveBar dirty={applyGroups.length > 0} saving={saving} onSave={save} />
    </>
  );
}

export default ModelsTab;
